import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Phone, MessageCircle, CheckCircle2, Sparkles, Award, ShieldCheck } from "lucide-react";
import * as Icons from "lucide-react";
import SEO from "../components/SEO";
import AppointmentDialog from "../components/AppointmentDialog";
import DoctorsStrip from "../components/DoctorsStrip";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import {
  SITE,
  ASSETS,
  DOCTORS,
  WHY_CHOOSE_US,
  HOMEPAGE_FAQS,
  callLink,
  whatsappLink,
} from "../lib/constants";
import { SERVICES } from "../data/services";
import useReveal from "../hooks/useReveal";

const TRUST_LOGOS = [
  "Osstem Certified",
  "MDS Specialists",
  "Digital X-Ray",
  "Sterilized & Safe",
  "EMI Available",
];

export default function Home() {
  useReveal();
  const [dialogOpen, setDialogOpen] = useState(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Dentist",
    name: SITE.name,
    image: ASSETS.logo,
    "@id": "https://lotusfamilydental.me",
    url: "https://lotusfamilydental.me",
    telephone: SITE.phone,
    priceRange: "₹₹",
    address: {
      "@type": "PostalAddress",
      streetAddress: "UG4 Ali Tower, MG Marg",
      addressLocality: "Civil Lines, Prayagraj",
      addressRegion: "Uttar Pradesh",
      postalCode: "211001",
      addressCountry: "IN",
    },
    geo: { "@type": "GeoCoordinates", latitude: 25.45, longitude: 81.83 },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "10:00",
      closes: "21:00",
    },
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "10" },
    sameAs: ["https://maps.app.goo.gl/BoGqU22ptkiW3oTp9"],
  };
  // Remove aggregateRating since user wants no fabricated review counts
  delete structuredData.aggregateRating;

  const faqStructured = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: HOMEPAGE_FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <SEO
        title="Best Dental Clinic in Allahabad | Best Dentist in Prayagraj - Lotus Family Dental"
        description="Trusted dental clinic in Allahabad offering dental implants, root canal treatment, braces, teeth whitening, and emergency dental care."
        keywords="best dental clinic in allahabad, dentist in prayagraj, dental implants, root canal allahabad, braces prayagraj"
        canonical="https://lotusfamilydental.me/"
        structuredData={[structuredData, faqStructured]}
      />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden">
        {/* Background ornaments */}
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-teal-100/40 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-teal-50/60 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 lg:pt-14 pb-16 lg:pb-24">
          {/* Doctors strip — sits directly above the H1 */}
          <div className="mb-10 lg:mb-14">
            <DoctorsStrip />
          </div>

          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-50 border border-teal-100 text-teal-700 text-xs font-medium mb-6">
                <Sparkles className="w-3.5 h-3.5" />
                LOTUS FAMILY DENTAL — Prayagraj
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-900 leading-[1.05]">
                Best Dental Clinic in{" "}
                <span className="font-medium text-teal-700">Allahabad</span>{" "}
                for Advanced & Painless Dental Care
              </h1>
              <div className="mt-5 flex items-center gap-3">
                <div className="h-px w-10 bg-teal-600" />
                <div className="font-heading text-lg sm:text-xl font-medium tracking-[0.18em] uppercase text-teal-700">
                  Lotus Family Dental
                </div>
              </div>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl">
                Experienced specialists providing modern treatments including implants, root canal, braces,
                whitening, and emergency dental care in Prayagraj.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => setDialogOpen(true)}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-teal-600 text-white font-medium hover:bg-teal-700 transition-all shadow-[0_8px_30px_-4px_rgba(13,148,136,0.4)] hover:shadow-[0_16px_40px_-8px_rgba(13,148,136,0.5)] hover:-translate-y-0.5"
                  data-testid="hero-book-button"
                >
                  Book Appointment <ArrowRight className="w-4 h-4" />
                </button>
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-slate-200 text-slate-900 font-medium hover:bg-slate-50 transition-all"
                  data-testid="hero-whatsapp-button"
                >
                  <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp
                </a>
                <a
                  href={callLink}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-slate-200 text-slate-900 font-medium hover:bg-slate-50 transition-all"
                  data-testid="hero-call-button"
                >
                  <Phone className="w-4 h-4 text-teal-600" /> {SITE.phone}
                </a>
              </div>

              {/* Trust ribbon */}
              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-teal-600" /> Sterilized & safe
                </div>
                <div className="flex items-center gap-1.5">
                  <Award className="w-4 h-4 text-teal-600" /> MDS specialists
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-teal-600" /> Same-day appointments
                </div>
              </div>
            </div>

            {/* Hero visual */}
            <div className="lg:col-span-5 relative">
              <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden bg-slate-100 shadow-2xl shadow-slate-300/40 reveal">
                <img
                  src={ASSETS.drRahul}
                  alt="Dr. Rahul Tiwari — Lotus Family Dental Allahabad"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-x-4 bottom-4 glass rounded-2xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center text-white">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-teal-700 font-medium">
                      Dr. Rahul Tiwari
                    </div>
                    <div className="text-sm font-medium text-slate-900">
                      BDS, MDS · Orthodontist & Implantologist
                    </div>
                  </div>
                </div>
              </div>
              <div className="hidden lg:block absolute -top-6 -left-6 w-32 h-32 rounded-3xl bg-white shadow-xl border border-slate-100 p-3 -rotate-6">
                <img
                  src={ASSETS.clinicEquipment}
                  alt="Modern dental equipment"
                  className="w-full h-full object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ============ TRUST RIBBON ============ */}
      <section className="border-y border-slate-100 bg-slate-50/50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-xs uppercase tracking-[0.22em] text-slate-500">
            {TRUST_LOGOS.map((l) => (
              <span key={l} className="font-medium">
                {l}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SERVICES GRID ============ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl reveal">
            <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold mb-3">
              Treatments we offer
            </div>
            <h2 className="font-heading text-3xl lg:text-5xl font-light tracking-tight text-slate-900">
              Complete dental care, <span className="text-teal-700 font-medium">under one roof.</span>
            </h2>
            <p className="mt-5 text-lg text-slate-600">
              From routine cleanings to advanced implants — we bring you global-standard treatment in the
              heart of Prayagraj.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => {
              const Icon = Icons[s.icon] || Icons.Sparkles;
              return (
                <Link
                  to={`/services/${s.slug}`}
                  key={s.slug}
                  className="reveal premium-card group bg-white border border-slate-100 rounded-3xl p-7 flex flex-col"
                  data-testid={`home-service-${s.slug}`}
                  style={{ transitionDelay: `${i * 40}ms` }}
                >
                  <div className="w-12 h-12 rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center group-hover:bg-teal-600 group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-xl font-medium text-slate-900 mt-5">{s.name}</h3>
                  <p className="text-sm text-slate-500 mt-1">{s.tagline}</p>
                  <p className="text-sm text-slate-600 mt-3 line-clamp-2 flex-1">{s.intro.slice(0, 110)}…</p>
                  <div className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-teal-700 group-hover:gap-2 transition-all">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ WHY CHOOSE US ============ */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4 lg:sticky lg:top-28 self-start reveal">
              <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold mb-3">
                Why choose us
              </div>
              <h2 className="font-heading text-3xl lg:text-5xl font-light tracking-tight text-slate-900">
                Why patients choose our dental clinic in <span className="text-teal-700 font-medium">Prayagraj</span>.
              </h2>
              <p className="mt-5 text-slate-600">
                We blend medical expertise, gentle care, and a calm clinic environment so every visit feels
                effortless.
              </p>
              <button
                onClick={() => setDialogOpen(true)}
                className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-teal-600 text-white text-sm font-medium hover:bg-teal-700"
                data-testid="why-book-button"
              >
                Book a visit <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
              {WHY_CHOOSE_US.map((w, i) => {
                const Icon = Icons[w.icon] || Icons.Star;
                return (
                  <div
                    key={i}
                    className="reveal bg-white rounded-3xl p-6 border border-slate-100 premium-card"
                    style={{ transitionDelay: `${i * 40}ms` }}
                  >
                    <div className="w-11 h-11 rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center">
                      <Icon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading text-lg font-medium text-slate-900 mt-4">{w.title}</h3>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed">{w.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============ DOCTORS ============ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl reveal">
            <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold mb-3">
              Meet our specialists
            </div>
            <h2 className="font-heading text-3xl lg:text-5xl font-light tracking-tight text-slate-900">
              Doctors who actually <span className="text-teal-700 font-medium">listen.</span>
            </h2>
            <p className="mt-5 text-lg text-slate-600">
              An MDS orthodontist & implantologist + a gold-medalist pedodontist — both associate professors
              at RKDF Dental College.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6 lg:gap-8">
            {DOCTORS.map((d, i) => (
              <div
                key={i}
                className="reveal group relative rounded-3xl overflow-hidden bg-white border border-slate-100 premium-card"
                data-testid={`doctor-card-${i}`}
              >
                <div className="aspect-[4/5] sm:aspect-[16/10] md:aspect-[4/5] overflow-hidden bg-slate-100">
                  <img
                    src={d.image}
                    alt={d.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-6 lg:p-8">
                  <div className="text-xs uppercase tracking-[0.18em] text-teal-600 font-semibold">
                    {d.specialty}
                  </div>
                  <h3 className="font-heading text-2xl font-medium text-slate-900 mt-2">{d.name}</h3>
                  <p className="text-sm text-slate-500 mt-1">{d.qualifications}</p>
                  <ul className="mt-4 space-y-1.5">
                    {d.titles.map((t, k) => (
                      <li key={k} className="text-sm text-slate-600 flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CLINIC GALLERY ============ */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="reveal">
              <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold mb-3">
                Inside our clinic
              </div>
              <h2 className="font-heading text-3xl lg:text-5xl font-light tracking-tight text-slate-900">
                A clinic that feels like a <span className="text-teal-700 font-medium">premium spa</span>.
              </h2>
              <p className="mt-5 text-lg text-slate-600">
                Sterilized, calm, and beautifully designed — our Civil Lines clinic is fitted with the latest
                Korean & European dental technology to give you world-class care.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Digital X-Ray (RVG) & intraoral cameras",
                  "Osstem implant placement system",
                  "Autoclave sterilization + single-use disposables",
                  "Comfortable, child-friendly treatment rooms",
                ].map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[480px] lg:h-[560px]">
              <div className="reveal col-span-4 row-span-4 rounded-3xl overflow-hidden bg-slate-200">
                <img src={ASSETS.clinicEquipment} alt="Dental equipment" className="w-full h-full object-cover" />
              </div>
              <div className="reveal col-span-2 row-span-3 rounded-3xl overflow-hidden bg-slate-200">
                <img src={ASSETS.clinicWall} alt="Clinic interior" className="w-full h-full object-cover" />
              </div>
              <div className="reveal col-span-2 row-span-3 rounded-3xl overflow-hidden bg-slate-200">
                <img src={ASSETS.modernRoom1} alt="Treatment room" className="w-full h-full object-cover" />
              </div>
              <div className="reveal col-span-4 row-span-2 rounded-3xl overflow-hidden bg-slate-200">
                <img src={ASSETS.modernRoom2} alt="Dental chair" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center reveal">
            <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold mb-3">
              Frequently asked questions
            </div>
            <h2 className="font-heading text-3xl lg:text-5xl font-light tracking-tight text-slate-900">
              Everything you wanted to ask.
            </h2>
            <p className="mt-4 text-slate-600">
              Still have a question? <a href={callLink} className="text-teal-700 font-medium underline-offset-4 hover:underline">Call us</a> — we love helping.
            </p>
          </div>

          <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="home-faq">
            {HOMEPAGE_FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-white border border-slate-100 rounded-2xl px-5 sm:px-6"
              >
                <AccordionTrigger className="text-left text-base font-medium text-slate-900 py-5 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ============ CONTACT BANNER ============ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-[2.5rem] overflow-hidden bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 p-8 sm:p-12 lg:p-16 text-white">
            <div className="absolute inset-0 opacity-30 dot-pattern" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.25) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-teal-200 font-semibold mb-3">
                  Visit us today
                </div>
                <h2 className="font-heading text-3xl lg:text-5xl font-light tracking-tight">
                  Ready for the smile you have always wanted?
                </h2>
                <p className="mt-5 text-teal-50 text-lg">
                  Walk in for a free consultation. We are open all 7 days from {SITE.timings}.
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <button
                    onClick={() => setDialogOpen(true)}
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-teal-700 font-medium hover:bg-teal-50 transition"
                    data-testid="bottom-book-button"
                  >
                    Book Appointment <ArrowRight className="w-4 h-4" />
                  </button>
                  <a
                    href={callLink}
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-transparent border border-white/30 text-white font-medium hover:bg-white/10 transition"
                  >
                    <Phone className="w-4 h-4" /> {SITE.phone}
                  </a>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {[
                  { label: "Clinic Address", value: SITE.address },
                  { label: "Open Hours", value: `${SITE.daysOpen} • ${SITE.timings}` },
                  { label: "WhatsApp", value: SITE.phone },
                ].map((c, i) => (
                  <div key={i} className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/10">
                    <div className="text-xs uppercase tracking-[0.18em] text-teal-200 font-medium">
                      {c.label}
                    </div>
                    <div className="mt-1 text-white">{c.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <AppointmentDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
}
